import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridActionsCell } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';

import { TWO_ACTIONS_COL_DEF_PROPS } from '@cbridge-tools/default-data-grid';
import { AlertType } from '@cbridge-packages/alert';
import { useIsReadOnly } from '@cbridge-packages/auth';

import { IEmailRecipient } from '../_components/AlertEmailRecipientsDataGrid/AlertEmailRecipients.interface';
import { AlertEmailSubscriptionCell } from '../_components/AlertEmailRecipientsDataGrid/AlertEmailSubscriptionCell';
import { DeleteEmailButton } from '../_components/DeleteEmailButton/DeleteEmailButton';

interface IUseEmailRecipientsGridColDefParams {
  alertTypes: AlertType[];
}

export const useEmailRecipientsGridColDef = ({ alertTypes }: IUseEmailRecipientsGridColDefParams) => {
  const { t } = useTranslation();
  const isReadonly = useIsReadOnly();

  const columns: GridColDef<IEmailRecipient>[] = useMemo(
    () => [
      {
        field: 'email',
        headerName: t('text:entity.user.email.label'),
        flex: 1,
      },
      ...alertTypes.map((alertType) => ({
        field: `subscriptions.${alertType}`,
        headerName: t(`text:entity.alert.type.value.${alertType}`),
        flex: 0,
        width: 200,
        renderCell: ({ row }) => (
          <AlertEmailSubscriptionCell
            email={row.email}
            alertType={alertType}
            checked={row.subscriptions[alertType]}
          />
        ),
      } as GridColDef<IEmailRecipient>)),
      ...(isReadonly
        ? []
        : [{
          ...TWO_ACTIONS_COL_DEF_PROPS,
          headerName: t('text:common.field.action.label'),
          renderCell: (params) => (
            <GridActionsCell {...params} suppressChildrenValidation={true}>
              <DeleteEmailButton email={params.row.email} />
            </GridActionsCell>
          ),
        }]
      ),
    ],
    [t, isReadonly, alertTypes],
  );

  return columns;
};
